import { FitnessPlan, getMuscleVolumeForPlan } from '@cf-shared/data-model/fitness-plan';
import { subSlotUsesMyoReps } from '@cf-shared/data-model/exercise-myoreps';
import { DeclaredState, FitnessState, getWorkoutsPerWeek } from '@cf-shared/data-model/user-state';
import { getExerciseConstantsTable, MuscleFactors } from '@cf-shared/workout-solver-interface';
// type-only, so this cannot create a runtime import cycle with the service
import type { MuscleVolumeEntry } from 'services/workout-loader.service';
import { buildMuscleVolumeRows } from './muscle-volume-rows';
import { CustomExercise, ExerciseDbService } from 'services/exercise-db.service';
import { buildMuscleFactorOverridesForPlanAsync } from './muscle-attribution';
import { UserDb } from 'services/user-db.service';
import { DocWatcher } from 'services/database-wrapper';
import { LoggingService } from 'services/logging.service';
import { LL } from 'i18n/i18n.service';
import { buildOwnExerciseAttributions, OwnExerciseAttribution, reportOrphanSubSlotIds, reportUncountedSubSlotIds } from './orphan-subslot-reporter';
// re-export so existing call sites (workout-loader) can keep importing from this module
export { reportOrphanSubSlotIds } from './orphan-subslot-reporter';

// Build the per-sub-slot muscle-factor overrides for a plan: a user's own exercise borrows
// the muscles of its "Most Similar Exercise", and a curated exercise sitting in a slot with
// no muscle weights borrows the muscles of the slot the catalog says it belongs to.
// This is only the wiring: the rules live in `muscle-attribution.ts`, which imports no
// Angular service so that it can be unit-tested directly.
export async function buildMuscleFactorOverridesAsync(
  fitnessPlan: FitnessPlan,
  customExercises: CustomExercise[],
  exerciseDb: ExerciseDbService,
  watcher: DocWatcher,
  sourceSlots?: { [subSlotId: string]: string },
  similarSlots?: { [customExerciseId: string]: string },
): Promise<{ [subSlotId: string]: MuscleFactors } | undefined> {
  return buildMuscleFactorOverridesForPlanAsync(fitnessPlan, getExerciseConstantsTable(), {
    getCustomExercisesAsync: async () => customExercises,
    slotShortIdForExerciseAsync: async exerciseId => {
      const slot = await exerciseDb.slotForGlobalExerciseIdAsync(exerciseId, watcher);
      return slot && slot._id.replace('slot/', '');
    },
  }, sourceSlots, similarSlots);
}

// Work out which sub-slots are scheduled as myo-reps (one activation set plus mini-sets)
// rather than classic straight sets. Callers hand the result to `getMuscleVolumeForPlan`
// and `reoptimizeVolumeAsync`, which need it to decide whether a stored volume adjustment
// counts: the workout builder never applies one to a myo-rep exercise, so neither may a
// screen that reports what the user's workouts hold (#4523).
//
// This lives here, beside buildMuscleFactorOverridesAsync, because it is the same shape of
// job — a per-sub-slot map that only a caller holding the databases can build.
export async function buildUsesMyoRepsBySubSlotAsync(
  fitnessPlan: FitnessPlan,
  declaredState: DeclaredState,
  fitnessState: FitnessState,
  customExercises: CustomExercise[],
  exerciseDb: ExerciseDbService,
  watcher: DocWatcher,
): Promise<{ [subSlotId: string]: boolean }> {
  const usesMyoRepsBySubSlotId: { [subSlotId: string]: boolean } = {};

  // for every sub-slot in the plan
  for (const subSlotId in fitnessPlan.subSlots) {
    const subSlot = fitnessPlan.subSlots[subSlotId];
    if (!subSlot?.exercise_id) continue;

    const exercise = await exerciseDb.getGlobalExerciseByIdAsync(subSlot.exercise_id, watcher)
      || customExercises.find(e => e._id === subSlot.exercise_id);
    // an exercise we cannot load is left out of the map, which reads as classic sets —
    // the same answer every caller got before this map existed. the reporting read is kept on
    // purpose: several screens build this map just to show numbers, and the one save it feeds
    // (the volume optimizer) uses it only to decide which counts it lists as changed
    // (df-c21e11979ef2)
    if (!exercise) continue;

    usesMyoRepsBySubSlotId[subSlotId] = subSlotUsesMyoReps(subSlot, exercise, declaredState, fitnessState);
  }

  return usesMyoRepsBySubSlotId;
}

// Build BOTH per-sub-slot maps the muscle-volume calculator and the volume optimizer need.
//
// They are built together because they must always travel together. Passing the muscle-factor
// overrides without the myo-rep map does not fail — it silently counts a volume adjustment for
// a myo-rep exercise, which the workout builder never applies, so one screen disagrees with
// another about the same muscle. Building them separately is how two call sites were missed
// when #4523 landed; one function returning both leaves nothing to forget.
//
// The user's own exercises are read once here and the same list is handed to every builder.
// A new per-exercise input should take that list too, rather than reading the table again.
export async function buildMuscleVolumeInputsAsync(
  fitnessPlan: FitnessPlan,
  declaredState: DeclaredState,
  fitnessState: FitnessState,
  userDb: UserDb,
  exerciseDb: ExerciseDbService,
  watcher: DocWatcher,
): Promise<MuscleVolumeInputs> {
  const customExercises = await userDb.getAllCustomExercisesAsync();
  const muscleFactorSourceSlots: { [subSlotId: string]: string } = {};
  // the slot of each own exercise's "Most Similar Exercise", taken from the same lookup the
  // overrides use, so the uncounted-exercise report and the volume numbers cannot disagree
  const similarSlots: { [customExerciseId: string]: string } = {};
  const muscleFactorOverrides = await buildMuscleFactorOverridesAsync(fitnessPlan, customExercises, exerciseDb, watcher, muscleFactorSourceSlots, similarSlots);
  const usesMyoRepsBySubSlotId = await buildUsesMyoRepsBySubSlotAsync(
    fitnessPlan, declaredState, fitnessState, customExercises, exerciseDb, watcher,
  );
  // what the uncounted-exercise report needs to tell an expected gap from a fault (#6060)
  const ownExercises = buildOwnExerciseAttributions(customExercises, similarSlots);
  return { muscleFactorOverrides, muscleFactorSourceSlots, usesMyoRepsBySubSlotId, ownExercises };
}

export interface MuscleVolumeInputs {
  muscleFactorOverrides: { [subSlotId: string]: MuscleFactors } | undefined;
  /** the slot each override was copied from; the solver reads its prime movers (#5398) */
  muscleFactorSourceSlots: { [subSlotId: string]: string };
  usesMyoRepsBySubSlotId: { [subSlotId: string]: boolean };
  /** the user's own exercises, by id; says which name a "Most Similar Exercise" (#6060) */
  ownExercises: { [exerciseId: string]: OwnExerciseAttribution };
}

// Builds the per-muscle volume display entries for the edit-workouts and program-settings pages.
// Side effect: reports any orphan subSlotIds (workout references with no plan.subSlots entry)
// to LoggingService — see reportOrphanSubSlotIds and #3282.
// Takes the whole `MuscleVolumeInputs` rather than its fields, for the reason given on
// `buildMuscleVolumeInputsAsync`: the maps must always travel together.
export function buildMuscleVolumeEntries(fitnessPlan: FitnessPlan, declaredState: DeclaredState, logging: LoggingService, inputs: MuscleVolumeInputs): {
  lowerBodyMuscles: MuscleVolumeEntry[];
  upperBodyMuscles: MuscleVolumeEntry[];
  // #3790: subSlotIds the calculator could not attribute to any muscle (typically custom
  // exercises with no "Most Similar Exercise" set). The UI surfaces these so the user knows
  // some exercises are not included in the totals.
  uncountedSubSlotIds: string[];
} {
  const lowerBodyMuscles: MuscleVolumeEntry[] = [];
  const upperBodyMuscles: MuscleVolumeEntry[] = [];
  const uncountedSubSlotIds: string[] = [];

  if (fitnessPlan.workouts && fitnessPlan.workouts.length > 0) {
    reportOrphanSubSlotIds(fitnessPlan, logging, 'buildMuscleVolumeEntries');
    const workoutsPerWeek = getWorkoutsPerWeek(declaredState);
    const muscleVolume = getMuscleVolumeForPlan(fitnessPlan, workoutsPerWeek, inputs.muscleFactorOverrides, uncountedSubSlotIds, inputs.usesMyoRepsBySubSlotId);
    if (uncountedSubSlotIds.length > 0) {
      reportUncountedSubSlotIds(uncountedSubSlotIds, fitnessPlan, inputs.ownExercises, logging, 'buildMuscleVolumeEntries');
    }

    const rows = buildMuscleVolumeRows(muscleVolume);
    lowerBodyMuscles.push(...rows.lowerBodyMuscles);
    upperBodyMuscles.push(...rows.upperBodyMuscles);
  }

  return { lowerBodyMuscles, upperBodyMuscles, uncountedSubSlotIds };
}

export function formatMuscleVolumeSummary(
  lowerBodyMuscles: MuscleVolumeEntry[],
  upperBodyMuscles: MuscleVolumeEntry[],
  maxDisplay = 3
): string {
  const all = [...upperBodyMuscles, ...lowerBodyMuscles]
    .sort((a, b) => b.volume - a.volume);

  if (all.length === 0) return '';

  const displayed = all.slice(0, maxDisplay);
  const setsLabel = LL.training.trainingProgram.sets();
  const parts = displayed.map(m => `${m.name} ${m.volume} ${setsLabel}`);
  const remaining = all.length - maxDisplay;

  if (remaining > 0) {
    parts.push(`+${remaining}`);
  }

  return parts.join(' · ');
}
